import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Send,
  Smile,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  X
} from 'lucide-react';
import { SocialPlatform, PLATFORM_INFO } from '@/types/social';
import { cn } from '@/lib/core/utils';
import { toast } from 'sonner';

interface MessageInputProps {
  onSend: (content: string) => Promise<void>;
  platform: SocialPlatform;
  disabled?: boolean;
}

// Common emoji selections
const QUICK_EMOJIS = ['😊', '👍', '❤️', '😂', '🙏', '👏', '🎉', '💯', '🔥', '✨'];

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  platform,
  disabled = false
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const platformInfo = PLATFORM_INFO[platform];
  const maxLength = platformInfo.maxContentLength;
  const isOverLimit = message.length > maxLength;

  const handleSend = async () => {
    if (!message.trim() || isSending || isOverLimit || disabled) return;

    setIsSending(true);
    try {
      await onSend(message.trim());
      setMessage('');
      textareaRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const insertEmoji = (emoji: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = message.slice(0, start) + emoji + message.slice(end);

    setMessage(newMessage);

    // Set cursor position after emoji
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
      textarea.focus();
    }, 0);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        {/* Text Input */}
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className={cn(
              "min-h-[44px] max-h-[120px] resize-none pr-20",
              isOverLimit && "border-red-500 focus-visible:ring-red-500"
            )}
            disabled={disabled || isSending}
            rows={1}
          />

          {/* Character count */}
          {message.length > 0 && (
            <div className={cn(
              "absolute bottom-2 right-2 text-xs",
              isOverLimit ? "text-red-500" : "text-muted-foreground"
            )}>
              {message.length}/{maxLength}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          {/* Emoji Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                disabled={disabled || isSending}
              >
                <Smile className="h-5 w-5 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="end">
              <div className="flex flex-wrap gap-1 max-w-[200px]">
                {QUICK_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => insertEmoji(emoji)}
                    className="text-xl hover:bg-accent p-1 rounded transition-colors"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={!message.trim() || isSending || isOverLimit || disabled}
            size="icon"
            className="h-9 w-9"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Error message for character limit */}
      {isOverLimit && (
        <p className="text-xs text-red-500">
          Message exceeds {platformInfo.name}'s character limit of {maxLength}
        </p>
      )}

      {/* Keyboard shortcut hint */}
      <p className="text-xs text-muted-foreground">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
};

export default MessageInput;
